/**
 * Expiração do carrinho por inatividade.
 *
 * Após CART_EXPIRY_MINUTES sem nenhuma interação com o carrinho
 * (adicionar/remover/alterar item), o carrinho é esvaziado silenciosamente —
 * sem mensagem para o usuário.
 *
 * - O contador reinicia a cada evento `PUB_SUB_EVENTS.cartUpdate`.
 * - A verificação roda no carregamento da página, periodicamente enquanto a
 *   aba está visível e ao retornar o foco para a aba (timers de background
 *   sofrem throttling, então reavaliamos nesses momentos).
 *
 * Depende de: window.routes (theme.liquid), pubsub.js, constants.js.
 */
(function () {
  const CART_EXPIRY_MINUTES = 15;
  const EXPIRY_MS = CART_EXPIRY_MINUTES * 60 * 1000;
  const STORAGE_KEY = 'noway:cart-last-activity';
  const CHECK_INTERVAL_MS = 30 * 1000;
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  let expiryTimeout = null;
  let isExpiring = false;

  /** @returns {string} */
  function cartBaseUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  /** @returns {number | null} timestamp em ms ou null se ausente/inválido */
  function readTimestamp() {
    try {
      const value = parseInt(localStorage.getItem(STORAGE_KEY), 10);
      return Number.isFinite(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  /** @param {number} value */
  function writeTimestamp(value) {
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch (error) {
      /* localStorage indisponível (modo privado) — ignora */
    }
  }

  function clearTimestamp() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* ignora */
    }
  }

  /** @returns {Promise<number>} */
  async function getCartItemCount() {
    try {
      const response = await fetch(`${cartBaseUrl()}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return 0;
      const cart = await response.json();
      return cart.item_count || 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Reaplica o HTML das seções de carrinho retornadas pela Section Rendering API.
   * @param {Record<string, string> | undefined} sections
   */
  function applySections(sections) {
    if (!sections) return;

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      drawer.getSectionsToRender().forEach((section) => {
        const element = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (element && sections[section.id]) {
          element.innerHTML = drawer.getSectionInnerHTML(sections[section.id], section.selector);
        }
      });
      drawer.classList.add('is-empty');
      const inner = drawer.querySelector('.drawer__inner');
      if (inner) inner.classList.add('is-empty');
      return;
    }

    // Sem drawer (cart_type page/notification): atualiza ao menos o ícone do carrinho.
    const bubbleHTML = sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHTML && bubble) {
      const parsed = new DOMParser().parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
      if (parsed) bubble.innerHTML = parsed.innerHTML;
    }
  }

  function cancelScheduledExpiry() {
    if (expiryTimeout) {
      clearTimeout(expiryTimeout);
      expiryTimeout = null;
    }
  }

  async function expireCart() {
    if (isExpiring) return;
    isExpiring = true;
    cancelScheduledExpiry();

    try {
      if ((await getCartItemCount()) === 0) {
        clearTimestamp();
        return;
      }

      const response = await fetch(`${cartBaseUrl()}/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ sections: SECTIONS, sections_url: window.location.pathname }),
      });
      if (!response.ok) return;

      const state = await response.json();
      clearTimestamp();

      // Na página do carrinho, recarrega para refletir o estado vazio por completo.
      if (window.location.pathname === cartBaseUrl()) {
        window.location.reload();
        return;
      }
      applySections(state.sections);
    } catch (error) {
      /* mantém o carrinho; nova tentativa no próximo ciclo */
    } finally {
      isExpiring = false;
    }
  }

  function scheduleFromTimestamp() {
    cancelScheduledExpiry();
    const last = readTimestamp();
    if (last === null) return;

    const remaining = last + EXPIRY_MS - Date.now();
    if (remaining <= 0) {
      expireCart();
    } else {
      expiryTimeout = setTimeout(expireCart, remaining);
    }
  }

  function recordActivity() {
    writeTimestamp(Date.now());
    scheduleFromTimestamp();
  }

  async function init() {
    // Carrinhos criados antes deste recurso (ou em outra aba/dispositivo) não têm
    // timestamp: começam a contar a partir de agora.
    if (readTimestamp() === null && (await getCartItemCount()) > 0) {
      writeTimestamp(Date.now());
    }
    scheduleFromTimestamp();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, recordActivity);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleFromTimestamp();
  });
  window.addEventListener('pageshow', scheduleFromTimestamp);
  setInterval(scheduleFromTimestamp, CHECK_INTERVAL_MS);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
